import { useMemo } from 'react';
import { FilterInputsList } from 'modules/journey/components/FilterInputsList';
import { DEFAULT_SEARCH_DATATYPE } from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';
import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { getDefaultPropertyMapConfig } from 'modules/journey/components/FilterInputsList/FilterInputsList.utils';
import { AND } from 'utils/combinatorTypes';
import { IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';
import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import { SearchQueryField } from 'modules/workflows/types';
import { SearchQueriesValidationErrors } from 'modules/journey/components/RuleBuilder/types';
import { useStartNodeContext } from '../StartNodeEditor.context';
import { useGetEntrySourceMetadata } from '../StartNodeEditor.hooks';
import { CombinatorSelect } from './CombinatorSelect';
import { COMBINATOR_TEXT, FILTERS_DISPLAY_TEXT, FILTERS_TEST_ID } from './EntryRules.constants';

type EntryRulesProps = {
  /** a validation error object to use for cloning */
  queriesErrors?: SearchQueriesValidationErrors;
};

/**
 * Visit the [slab documentation](https://iterable.slab.com/posts/start-tile-editor-components-wlds1ru4#hee0f-entry-rules)
 * for more information on Entry Rules
 * */
export const EntryRules = ({ queriesErrors }: EntryRulesProps) => {
  const { isReadOnly, nodeData, initialNodeData, setNodeData } = useStartNodeContext();
  const { searchCombo, nodeClass } = nodeData;

  const { combinator = AND, searchQueries = [] } = searchCombo || {};

  // Values derived from trigger metadata
  const {
    dataType = DEFAULT_SEARCH_DATATYPE,
    resourceFieldName,
    resourceFieldValue,
  } = useGetEntrySourceMetadata();

  // Check if the "Add additional entry rules" button should be disabled
  const isAddFilterDisabled =
    !!resourceFieldName && !resourceFieldValue && searchQueries?.length === 0;

  // Handle search queries update
  const onSearchQueriesUpdate = (newQueries: Partial<Query>[]) => {
    // we can't save when comparator type is IS_SET or IS_NOT_SET because the value is null
    // backend expects an empty string as value, so we manually add it
    const saveableNewQueries = newQueries.map((q) =>
      q.comparatorType === IS_SET || q.comparatorType === IS_NOT_SET ? { ...q, value: '' } : q,
    );

    setNodeData({
      searchCombo: {
        combinator,
        searchQueries: saveableNewQueries as SearchQueryField[],
      },
    });
  };

  // Generate allowed and disallowed event names based on data type
  const getSearchQueriesFiltersConfig = useMemo(
    () =>
      getDefaultPropertyMapConfig({
        // 'Shopping cart update' and 'User profile updated' use the same dataType so we need to check nodeClass here
        dataType:
          nodeClass === WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART
            ? WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART
            : dataType,
        filterString: resourceFieldValue,
        showInitialSelected: initialNodeData.nodeClass === nodeClass,
        initialSearchQueries: initialNodeData.searchCombo?.searchQueries as SearchQueryField[],
      }),
    [dataType, nodeClass, initialNodeData, resourceFieldValue],
  );

  return (
    <>
      {searchQueries?.length > 0 && <CombinatorSelect />}
      <div data-test={FILTERS_TEST_ID}>
        <FilterInputsList
          canAddFilter={!isReadOnly}
          canDelete={!isReadOnly}
          combinatorText={COMBINATOR_TEXT[combinator].conjunction}
          dataType={dataType}
          displayText={FILTERS_DISPLAY_TEXT}
          initialQueries={initialNodeData?.searchCombo?.searchQueries as Query[]}
          isDisabled={isReadOnly || isAddFilterDisabled}
          isDarkTheme
          onSearchQueriesUpdate={onSearchQueriesUpdate}
          propertyMapConfig={getSearchQueriesFiltersConfig}
          queries={searchQueries as Query[]}
          queriesErrors={queriesErrors}
          trigger={nodeClass}
        />
      </div>
    </>
  );
};
